import { CardCtaAction } from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useCardDetails from 'features/cardManagement/hooks/useCardDetails';
import useReplaceLostStolenCard from 'features/cardManagement/hooks/useReplaceLostStolenCard';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const ReplaceCardScreen = () => {
  const router = useRouter();
  const { cardId, cardType } = useLocalSearchParams<{
    cardId: string;
    cardType: string;
  }>();
  const { t: translatedText } = useTranslation('cardManagement', {
    keyPrefix: 'replaceCard',
  });
  const [cardAction, setCardAction] = useState<CardCtaAction | null>(null);

  const {
    cardReviewDetailsSuccess,
    cardReviewDetailsError,
    loading,
    togglePause,
    handleCloseBottomSheet,
    handlePresentBottomSheet,
    bottomSheetRef,
    cardReviewError,
  } = useCardDetails(cardId, cardAction ?? '');

  const {
    handleReplaceLostStolenCard,
    replaceCardDataFetching,
    replaceCardMutationError,
  } = useReplaceLostStolenCard(cardId, cardType);

  const address = cardReviewDetailsSuccess?.address?.address;

  const stateZip = [address?.state, address?.zip].filter(Boolean).join(' ');
  const addressLine3 = address
    ? [address.city, stateZip].filter(Boolean).join(', ')
    : '';

  const handleReviewCard = (cardAction: CardCtaAction | null) => {
    if (cardAction) {
      setCardAction(cardAction);
      togglePause();
    }
  };

  const sendNewCard = async () => {
    await handleReplaceLostStolenCard();
    handleCloseBottomSheet();
  };

  const errorResponse =
    cardReviewError || replaceCardMutationError || cardReviewDetailsError;

  return (
    <View style={styles.screenContent}>
      <Stack.Screen
        options={{
          title: translatedText('headerTitle'),
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
          headerShown: true,
          headerBackVisible: true,
        }}
      />
      <View style={styles.textContent}>
        <TitleLgText style={styles.lgText} numberOfLines={0}>
          {translatedText('title')}
        </TitleLgText>
        <BodyBaseText numberOfLines={0}>
          {translatedText('description')}
        </BodyBaseText>
      </View>

      <ListItem
        primaryCopy={translatedText('cardLost')}
        analyticsId="cardLostClicked"
        customAttributes={{
          card_type: cardType?.toLowerCase(),
          reason: translatedText('cardLost'),
        }}
        onPress={() => {
          handlePresentBottomSheet();
          handleReviewCard(CardCtaAction.OrderPhysicalCard);
        }}
      />
      <View style={{ paddingHorizontal: theme.spacing.m }}>
        <HairLineDivider />
      </View>
      <ListItem
        primaryCopy={translatedText('cardStolen')}
        analyticsId="cardStolenClicked"
        customAttributes={{
          card_type: cardType?.toLowerCase(),
          reason: translatedText('cardStolen'),
        }}
        onPress={() => {
          handlePresentBottomSheet();
          handleReviewCard(CardCtaAction.ReplaceLostOrStolenCard);
        }}
      />

      <BottomSheet ref={bottomSheetRef}>
        {loading ? (
          <View style={styles.bottomSheetContainer}>
            <LoadingSpinner />
          </View>
        ) : (
          <View style={styles.container}>
            <View style={styles.contentContainer}>
              <TitleMdText numberOfLines={0}>
                {translatedText('reviewDetails')}
              </TitleMdText>
              <View style={styles.reviewDetails}>
                <BodyBaseText numberOfLines={0}>
                  {cardReviewDetailsSuccess?.review?.description}
                </BodyBaseText>
                <View>
                  <BodyBaseText numberOfLines={0}>
                    {address?.addressLine1}
                  </BodyBaseText>
                  {address?.addressLine2 ? (
                    <BodyBaseText numberOfLines={0}>
                      {address.addressLine2}
                    </BodyBaseText>
                  ) : null}
                  <BodyBaseText numberOfLines={0}>{addressLine3}</BodyBaseText>
                </View>

                <BodyBaseText numberOfLines={0}>
                  {cardReviewDetailsSuccess?.arrival?.description}
                </BodyBaseText>
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <Button
                analyticsId="orderPhysicalCardClicked"
                customAttributes={{
                  card_type: cardType?.toLowerCase(),
                }}
                onPress={async () => {
                  await sendNewCard();
                }}
                size="LARGE"
                type="PRIMARY"
                width="FULL"
                label={translatedText('sendNewCard')}
                isLoading={replaceCardDataFetching}
              />

              <Button
                onPress={() => {
                  handleCloseBottomSheet();
                }}
                size="LARGE"
                type="SECONDARY"
                width="FULL"
                label={translatedText('goBack')}
              />
            </View>
          </View>
        )}
      </BottomSheet>

      {errorResponse ? (
        <ScreenTransitionOverlay
          isLoading={false}
          LoadingComponent={<LoadingSpinner />}
          ErrorComponent={
            <ErrorScreen
              screenType={
                cardReviewDetailsError
                  ? ErrorScreenType.Warning
                  : ErrorScreenType.Error
              }
              messageHeadline={
                cardReviewDetailsError?.title ?? translatedText('errorTitle')
              }
              messageBody={
                cardReviewDetailsError?.message ??
                translatedText('errorMessage')
              }
              primaryButton={{
                title: cardReviewDetailsError
                  ? translatedText('done')
                  : translatedText('retry'),
                onPress: () => {
                  if (cardReviewDetailsError) {
                    router.push(
                      `/myvaro/card/replace-lost-stolen/replace-card/${cardId}`,
                    );
                  }
                  if (cardReviewError) {
                    handleReviewCard(cardAction);
                  }
                  if (replaceCardMutationError) {
                    void sendNewCard();
                  }
                },
              }}
            />
          }
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheetContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    gap: theme.spacing.xs,
    padding: theme.spacing.m,
  },
  buttonContainer: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  container: {
    gap: theme.spacing.xl,
  },
  contentContainer: {
    gap: theme.spacing.s,
    paddingHorizontal: theme.spacing.m,
  },
  lgText: {
    marginBottom: theme.spacing.m,
  },
  reviewDetails: {
    gap: theme.spacing.xl,
  },
  screenContent: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  textContent: {
    alignContent: 'space-evenly',
    padding: theme.spacing.m,
  },
});

export default ReplaceCardScreen;
